import React, { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PeriodFilterBar from '../../components/Management/PeriodFilterBar';
import HealthBadge from '../../components/Management/HealthBadge';
import { LabelWithInfo } from '../../components/InfoTooltip';
import { managementHelp } from '../../content/managementHelp';
import { analyticsAPI } from '../../services/analytics';
import {
  trainerRankingLine1,
  trainerRankingLine2,
  trainerRankingDemoTrialLine,
  trainerRankingClosedLabel,
} from '../../utils/trainerRankingMetrics';
import {
  leadCohortLine1,
  leadCohortLine2,
  leadCohortDemoTrialLine,
  leadCohortActivityLabel,
} from '../../utils/leadCohortMetrics';
import {
  dimensionFromSourceRow,
  dimensionFromCountryRow,
  dimensionFromSalesRow,
} from '../../utils/leadListDeepLinks';
import { useAuth } from '../../contexts/AuthContext';

const TYPE_CONFIG = {
  sources: {
    title: 'Lead sources',
    fetch: (params) => analyticsAPI.getBySource(params),
    rowsKey: 'rows',
    getName: (row) => row.sourceGroup,
    getDimension: dimensionFromSourceRow,
    getLine1: (row, ctx) => leadCohortLine1(row, ctx),
    getLine2: (row) => leadCohortLine2(row),
    getLine3: (row, ctx) => leadCohortDemoTrialLine(row, ctx),
    getLine4: (row, ctx) => (
      <LabelWithInfo
        label={leadCohortActivityLabel(row, ctx)}
        title={managementHelp.sources.activity.title}
        body={managementHelp.sources.activity.body}
      />
    ),
  },
  countries: {
    title: 'Countries',
    fetch: (params) => analyticsAPI.getByCountry(params),
    rowsKey: 'rows',
    getName: (row) => row.country,
    getDimension: dimensionFromCountryRow,
    getLine1: (row, ctx) => leadCohortLine1(row, ctx),
    getLine2: (row) => leadCohortLine2(row),
    getLine3: (row, ctx) => leadCohortDemoTrialLine(row, ctx),
    getLine4: (row, ctx) => (
      <LabelWithInfo
        label={leadCohortActivityLabel(row, ctx)}
        title={managementHelp.countries.activity.title}
        body={managementHelp.countries.activity.body}
      />
    ),
  },
  sales: {
    title: 'Sales performance',
    fetch: (params) => analyticsAPI.getBySales(params),
    rowsKey: 'rows',
    getName: (row) => row.name,
    getDimension: dimensionFromSalesRow,
    getLine1: (row, ctx) => leadCohortLine1(row, ctx),
    getLine2: (row) => leadCohortLine2(row),
    getLine3: (row, ctx) => leadCohortDemoTrialLine(row, ctx),
    getLine4: (row, ctx) => (
      <LabelWithInfo
        label={leadCohortActivityLabel(row, ctx)}
        title={managementHelp.sales.activity.title}
        body={managementHelp.sales.activity.body}
      />
    ),
  },
  trainers: {
    title: 'Trainer demo conversion',
    fetch: (params) => analyticsAPI.getByTrainer(params),
    rowsKey: 'rows',
    getName: (row) => row.name,
    getDimension: () => ({}),
    getLine1: (row, ctx) => trainerRankingLine1(row, ctx),
    getLine2: (row) => trainerRankingLine2(row),
    getLine3: (row, ctx) => trainerRankingDemoTrialLine(row, ctx),
    getLine4: (row, ctx) => (
      <LabelWithInfo
        label={trainerRankingClosedLabel(row, ctx)}
        title={managementHelp.trainers.closedThisMonth.title}
        body={managementHelp.trainers.closedThisMonth.body}
      />
    ),
  },
};

const ManagementRankings = () => {
  const navigate = useNavigate();
  const { isAdmin, isTeacherAdmin } = useAuth();
  const trainersOnly = isTeacherAdmin() && !isAdmin();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedType = searchParams.get('type') || 'sources';
  // teacher_admin may only view trainer rankings
  const type = trainersOnly ? 'trainers' : requestedType;
  const period = searchParams.get('period') || 'this_month';
  const customFrom = searchParams.get('from') || '';
  const customTo = searchParams.get('to') || '';
  const config = TYPE_CONFIG[type] || TYPE_CONFIG.sources;

  const queryParams = useMemo(() => {
    const p = { period };
    if (period === 'custom' && customFrom && customTo) {
      p.from = customFrom;
      p.to = customTo;
    }
    return p;
  }, [period, customFrom, customTo]);

  const queryEnabled = period !== 'custom' || Boolean(customFrom && customTo);

  const { data: res, isLoading, error } = useQuery(
    ['managementRankings', type, queryParams],
    () => config.fetch(queryParams),
    { staleTime: 60000, enabled: queryEnabled }
  );

  const rows = res?.data?.data?.[config.rowsKey] || [];
  const reportPeriod = res?.data?.data?.period;
  const periodLabel = reportPeriod
    ? `${reportPeriod.from} → ${reportPeriod.to} (IST)`
    : '';

  const setPeriod = (nextPeriod, from, to) => {
    const next = new URLSearchParams(searchParams);
    next.set('type', type);
    next.set('period', nextPeriod);
    if (nextPeriod === 'custom' && from && to) {
      next.set('from', from);
      next.set('to', to);
    } else {
      next.delete('from');
      next.delete('to');
    }
    setSearchParams(next);
  };

  return (
    <Box>
      <PeriodFilterBar
        period={period}
        customFrom={customFrom}
        customTo={customTo}
        periodLabel={periodLabel}
        onPeriodChange={(p) => setPeriod(p)}
        onCustomFromChange={(v) => {
          const next = new URLSearchParams(searchParams);
          next.set('from', v);
          setSearchParams(next);
        }}
        onCustomToChange={(v) => {
          const next = new URLSearchParams(searchParams);
          next.set('to', v);
          setSearchParams(next);
        }}
        onApplyCustom={() => setPeriod('custom', customFrom, customTo)}
      />

      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/management')} sx={{ mb: 2 }}>
        {trainersOnly ? 'Back to Trainer performance' : 'Back to Sales & Enrollment'}
      </Button>
      <Typography variant="h5" component="h1" gutterBottom>
        {type === 'sales' ? (
          <LabelWithInfo
            label={config.title}
            title={managementHelp.sales.title}
            body={managementHelp.sales.body}
          />
        ) : type === 'trainers' ? (
          <LabelWithInfo
            label={config.title}
            title={managementHelp.trainers.title}
            body={managementHelp.trainers.body}
          />
        ) : (
          config.title
        )}
      </Typography>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}
      {error && <Alert severity="error">{error.message}</Alert>}

      {!isLoading && !error && (
        <Card>
          <CardContent>
            {rows.length === 0 && (
              <Typography color="text.secondary">No data for this period.</Typography>
            )}
            {rows.map((row, idx) => {
              const linkCtx = {
                period: reportPeriod,
                dimension: config.getDimension(row),
              };
              return (
                <Box
                  key={row.userId || row.teacherId || row.sourceGroup || row.country || idx}
                  sx={{
                    p: 2,
                    mb: 1,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                  }}
                >
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {config.getName(row)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" component="div">
                      {config.getLine1(row, linkCtx)}
                    </Typography>
                    <Typography variant="body2" component="div" sx={{ mt: 0.5 }}>
                      {config.getLine2(row)}
                    </Typography>
                    {config.getLine3 ? (
                      <Typography variant="body2" color="text.secondary" component="div" sx={{ mt: 0.25 }}>
                        {config.getLine3(row, linkCtx)}
                      </Typography>
                    ) : null}
                    {config.getLine4 ? (
                      <Typography variant="body2" color="text.secondary" component="div" sx={{ mt: 0.25 }}>
                        {config.getLine4(row, linkCtx)}
                      </Typography>
                    ) : null}
                  </Box>
                  <HealthBadge healthStatus={row.healthStatus} healthLabel={row.healthLabel} />
                </Box>
              );
            })}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default ManagementRankings;
